import { isEM, ExchangeMessage, ErrorExchangeMessage } from '../message';
import { DEBUG } from '../util';
import { responseTimeoutErrorMessage } from './messages';

/**
 * Interface of the native client for the exchange.
 * @internal
 */
export interface NativeExchangeClient {
  postMessage: (messageJSONString: string) => void;
}

/** WebKit Types **/
type MessageHandlerName = string;
type WebKitMessageHandlers = Record<MessageHandlerName, NativeExchangeClient>;

declare global {
  interface Window {
    webkit?: { messageHandlers: WebKitMessageHandlers };
    NME_NATIVE_CLIENT?: NativeExchangeClient;
    NME_WEB_CLIENT?: NativeExchangeClient;
    NativeMessageExchange?: typeof NativeMessageExchange;
  }
  interface WindowEventMap {
    [NATIVE_MESSAGE_EXCHANGE_EVENT_KEY]: CustomEvent<ExchangeMessage>;
  }
}

const NATIVE_MESSAGE_EXCHANGE_EVENT_KEY = 'native_message_exchange:message';

/** Number of seconds to wait for a response after seconding a message **/
const RESPONSE_TIMEOUT_SECONDS = 10;

export default class NativeMessageExchange {
  private static _nativeExchangeClient(): NativeExchangeClient {
    const iOSClient = window.webkit?.messageHandlers?.NME_NATIVE_CLIENT;

    if (typeof window.NME_NATIVE_CLIENT !== 'undefined') {
      return window.NME_NATIVE_CLIENT;
    } else if (typeof iOSClient !== 'undefined') {
      return iOSClient;
    } else {
      throw new Error('No Native Client Registered');
    }
  }

  /**
   * Asynchronously send a message to the native client, and wait for a
   * response.
   * A `RESPONSE_TIMEOUT` error message will be returned if no message is
   * received with a matching transaction id.
   *
   * This function will assign a `txId` to the given message
   *
   * @param message valid ExchangeMessage object to send to client
   * @typeParam R expected type of response message
   */
  static async sendMessageToNative<
    R extends ExchangeMessage<string, unknown, unknown>
  >(message: ExchangeMessage): Promise<R> {
    return new Promise((resolve, reject) => {
      if (!isEM(message)) {
        DEBUG('sendMessageToNative: Invalid Message');
        throw new Error('Invalid Message');
      }
      const txId = Date.now().toString();

      const timeoutSetTimeoutId = setTimeout(() => {
        reject(responseTimeoutErrorMessage({ txId }));
        window.removeEventListener(
          NATIVE_MESSAGE_EXCHANGE_EVENT_KEY,
          exchangeMessageHandler
        );
      }, RESPONSE_TIMEOUT_SECONDS * 1000);

      const exchangeMessageHandler = (event: CustomEvent<ExchangeMessage>) => {
        const responseMessage = event.detail;
        if (responseMessage.txId !== txId) return;

        DEBUG('exchangeMessageHandler:', JSON.stringify(responseMessage));

        responseMessage.error
          ? reject(responseMessage as ErrorExchangeMessage)
          : resolve(responseMessage as R);

        clearTimeout(timeoutSetTimeoutId);
        window.removeEventListener(
          NATIVE_MESSAGE_EXCHANGE_EVENT_KEY,
          exchangeMessageHandler
        );
      };

      window.addEventListener(
        NATIVE_MESSAGE_EXCHANGE_EVENT_KEY,
        exchangeMessageHandler,
        false
      );

      NativeMessageExchange._nativeExchangeClient().postMessage(
        JSON.stringify({ ...message, txId })
      );
    });
  }

  /**
   * Send a message to the native client, expecting no response.
   *
   * This function will assign a `txId` to the given message
   */
  static sendMessageToNativeSync(message: ExchangeMessage): void {
    if (!isEM(message)) {
      DEBUG('sendMessageToNative: Invalid Message');
      throw new Error('Invalid Message');
    }

    const txId = Date.now().toString();

    NativeMessageExchange._nativeExchangeClient().postMessage(
      JSON.stringify({ ...message, txId })
    );
  }

  /**
   * initialise the message exchange to be available on `window.NME_WEB_CLIENT`
   */
  static init(): void {
    window.NME_WEB_CLIENT = NativeMessageExchange;
  }

  /**
   * Sends a message to the web client.
   *
   * @param message JSON string of an ExchangeMessage compliant JSON object
   */
  static postMessage(message: string): void {
    DEBUG('postMessage:', message);
    const parsedMessage = JSON.parse(message);

    if (!isEM(parsedMessage)) {
      DEBUG('postMessage: Invalid Message');
      throw new Error('Invalid Message');
    }

    const event = new CustomEvent<ExchangeMessage>(
      NATIVE_MESSAGE_EXCHANGE_EVENT_KEY,
      {
        detail: parsedMessage,
      }
    );

    window.dispatchEvent(event);
  }
}
